/*
"Journal d'audit" — admin-only browse of the AuditLog table. Mirrors
the filter / pagination patterns of CardsPage and AdminUsersPage:

  - filter bar: action dropdown, actor-email substring, date range
  - debounced search (250ms)
  - URL-persistent filter state via useSearchParams
  - prev/next pagination, page-size selector

Calls GET /api/admin/audit-log on the server, which returns the
columns of AuditLog plus an `actorEmailLive` field showing the
current email of the actor user (or null if their account has been
deleted since). The "details" JSON column is rendered as a
collapsible <pre> block — most rows have a small object so the
default-open view is fine.
*/

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../lib/api";
import { ErrorState } from "../components/ErrorState";
import { Skeleton, SkeletonRows } from "../components/Skeleton";

const kPageSizeDefault	= 50;
const kPageSizeOptions	= [25, 50, 100, 200];
const kSearchDebounceMs	= 250;

// All values must match lib/audit.ts AuditAction.
const kActions =
[
	"LOGIN_SUCCESS",
	"LOGIN_FAILED",
	"LOGIN_CODE_REQUESTED",
	"ACCOUNT_LOCKED",
	"CARD_UPLOAD",
	"CARD_DELETE",
	"CARD_SHARE",
	"CARD_SHARE_REVOKE",
	"USER_PROFILE_UPDATE",
	"USER_SELF_DELETE",
	"ADMIN_USER_CREATE",
	"ADMIN_USER_PATCH",
	"ADMIN_USER_DELETE",
] as const;

interface AuditRow
{
	id:				string;
	at:				number;	// ms since epoch
	action:			string;
	actorUserId:	string | null;
	actorEmail:		string | null;
	actorEmailLive:	string | null;	// Current email if user still exists
	actorRole:		string | null;
	actorIp:		string | null;
	targetType:		string | null;
	targetId:		string | null;
	details:		unknown;
}

interface AuditPageResponse
{
	items:		AuditRow[];
	total:		number;
	page:		number;
	pageSize:	number;
}

interface Filters
{
	action:		string;	// Empty = no filter
	email:		string;	// Substring on actorEmail
	from:		string;	// "YYYY-MM-DD" or ""
	to:			string;	// "YYYY-MM-DD" or ""
}

const kEmptyFilters: Filters = { action: "", email: "", from: "", to: "" };

export function AuditLogPage(): JSX.Element
{
	const { t }		= useTranslation("audit");
	const { t: tc }	= useTranslation("common");

	const [vSearchParams, fSetSearchParams] = useSearchParams();

	const [fRows,  fSetRows]	= useState<AuditRow[] | null>(null);
	const [fTotal, fSetTotal]	= useState<number>(0);
	const [fPage,  fSetPage]	= useState<number>(1);
	const [fError, fSetError]	= useState<string | null>(null);
	const [fFilters,  fSetFilters]	= useState<Filters>(() => filtersFromParams(vSearchParams));
	const [fPageSize, fSetPageSize]	= useState<number>(() => readPageSizeFromParams(vSearchParams));

	const fFiltersRef	= useRef<Filters>(fFilters);
	const fPageSizeRef	= useRef<number>(fPageSize);

	useEffect(() => { fFiltersRef.current = fFilters; },   [fFilters]);
	useEffect(() => { fPageSizeRef.current = fPageSize; }, [fPageSize]);

	useEffect(() =>
	{
		fSetSearchParams(filtersToParams(fFilters, fPageSize), { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fFilters, fPageSize]);

	const fLoad = useCallback((inPage: number, inFilters: Filters, inPageSize: number) =>
	{
		const vParams = new URLSearchParams();
		vParams.set("page",     String(inPage));
		vParams.set("pageSize", String(inPageSize));

		if (inFilters.action !== "")	{ vParams.set("action",     inFilters.action); }
		if (inFilters.email.trim() !== "")	{ vParams.set("actorEmail", inFilters.email.trim()); }

		const vFromMs = dateInputToStartMs(inFilters.from);
		const vToMs	  = dateInputToEndMs(inFilters.to);

		if (vFromMs !== null) { vParams.set("from", String(vFromMs)); }
		if (vToMs   !== null) { vParams.set("to",   String(vToMs)); }

		api<AuditPageResponse>("GET", `/admin/audit-log?${vParams.toString()}`)
			.then((vResp) =>
			{
				fSetRows(vResp.items);
				fSetTotal(vResp.total);
				fSetPage(vResp.page);
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
	}, []);

	useEffect(() =>
	{
		const vTimer = window.setTimeout(() => fLoad(1, fFilters, fPageSize), kSearchDebounceMs);

		return () => window.clearTimeout(vTimer);
	}, [fFilters, fPageSize, fLoad]);

	if (fError !== null)
	{
		return (
			<ErrorState
				message={fError}
				onRetry={() => { fSetError(null); fLoad(fPage, fFiltersRef.current, fPageSizeRef.current); }}
			/>
		);
	}

	const vAnyActive = fFilters.action !== "" || fFilters.email !== "" || fFilters.from !== "" || fFilters.to !== "";
	const vTotalPages = Math.max(1, Math.ceil(fTotal / fPageSize));

	return (
		<div className="card">
			<h2>{t("title_count", { total: fTotal })}</h2>

			<div className="filter-bar">
				<div className="filter-bar__column">
					<select
						value={fFilters.action}
						onChange={(e) => fSetFilters({ ...fFilters, action: e.target.value })}
						aria-label={t("col_action")}
					>
						<option value="">{t("filter_action_all")}</option>
						{kActions.map((a) => <option key={a} value={a}>{a}</option>)}
					</select>
					<input
						type="text"
						placeholder={t("filter_actor")}
						value={fFilters.email}
						onChange={(e) => fSetFilters({ ...fFilters, email: e.target.value })}
						aria-label={t("filter_actor")}
					/>
				</div>

				<div className="filter-bar__divider" aria-hidden="true" />

				<div className="filter-bar__column">
					<label className="filter-bar__date-row">
						<span className="filter-bar__date-label">{tc("from")}</span>
						<input
							type="date"
							value={fFilters.from}
							onChange={(e) => fSetFilters({ ...fFilters, from: e.target.value })}
							aria-label={t("filter_date_min")}
						/>
					</label>
					<label className="filter-bar__date-row">
						<span className="filter-bar__date-label">{tc("to")}</span>
						<input
							type="date"
							value={fFilters.to}
							onChange={(e) => fSetFilters({ ...fFilters, to: e.target.value })}
							aria-label={t("filter_date_max")}
						/>
					</label>
				</div>

				{vAnyActive
					? <button type="button" className="button button--ghost" onClick={() => fSetFilters(kEmptyFilters)}>{tc("clear")}</button>
					: null}
			</div>

			{fRows === null
				? <div><Skeleton variant="title" widthPercent={25} /><SkeletonRows count={6} /></div>
				: fRows.length === 0
					? <p className="muted">{vAnyActive ? t("no_entries_filtered") : t("no_entries")}</p>
					: (
						<>
							<table>
								<thead>
									<tr>
										<th>{t("col_when")}</th>
										<th>{t("col_action")}</th>
										<th>{t("col_actor")}</th>
										<th>{t("col_ip")}</th>
										<th>{t("col_target")}</th>
										<th>{t("col_details")}</th>
									</tr>
								</thead>
								<tbody>
									{fRows.map((r) => (
										<tr key={r.id}>
											<td style={{ whiteSpace: "nowrap" }}>{formatDateTime(r.at)}</td>
											<td><code>{r.action}</code></td>
											<td>
												{r.actorEmail ?? <span className="muted">{tc("dash")}</span>}
												{r.actorEmailLive !== null && r.actorEmailLive !== r.actorEmail
													? <span className="muted"> {t("actor_now_suffix", { email: r.actorEmailLive })}</span>
													: null}
												{r.actorUserId === null && r.actorEmail !== null
													? <span className="muted" title={t("actor_deleted_tooltip")}> {t("actor_deleted_suffix")}</span>
													: null}
											</td>
											<td><code>{r.actorIp ?? tc("dash")}</code></td>
											<td>
												{r.targetType !== null && r.targetId !== null
													? <code>{r.targetType}:{r.targetId}</code>
													: <span className="muted">{tc("dash")}</span>}
											</td>
											<td>
												{r.details !== null && r.details !== undefined
													? <pre className="json-block" style={{ maxHeight: "8rem", margin: 0 }}>
														{JSON.stringify(r.details, null, 2)}
													</pre>
													: <span className="muted">{tc("dash")}</span>}
											</td>
										</tr>
									))}
								</tbody>
							</table>

							<div className="pager">
								<select
									className="pager__page-size"
									value={fPageSize}
									onChange={(e) => fSetPageSize(Number.parseInt(e.target.value, 10))}
									aria-label={tc("page_size_label")}
								>
									{kPageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
								</select>
								<button
									type="button"
									className="button button--ghost"
									disabled={fPage <= 1}
									onClick={() => fLoad(fPage - 1, fFiltersRef.current, fPageSizeRef.current)}
								>
									{tc("prev")}
								</button>
								<span className="muted">{tc("page_of", { page: fPage, total: vTotalPages })}</span>
								<button
									type="button"
									className="button button--ghost"
									disabled={fPage >= vTotalPages}
									onClick={() => fLoad(fPage + 1, fFiltersRef.current, fPageSizeRef.current)}
								>
									{tc("next")}
								</button>
							</div>
						</>
					)}
		</div>
	);
}

function filtersFromParams(p: URLSearchParams): Filters
{
	return {
		action:	p.get("action") ?? "",
		email:	p.get("actorEmail") ?? "",
		from:	p.get("from") ?? "",
		to:		p.get("to") ?? "",
	};
}

function filtersToParams(f: Filters, pageSize: number): URLSearchParams
{
	const p = new URLSearchParams();

	if (f.action !== "")		{ p.set("action", f.action); }
	if (f.email.trim() !== "")	{ p.set("actorEmail", f.email.trim()); }
	if (f.from !== "")			{ p.set("from", f.from); }
	if (f.to !== "")			{ p.set("to",   f.to); }
	if (pageSize !== kPageSizeDefault) { p.set("pageSize", String(pageSize)); }

	return p;
}

function readPageSizeFromParams(p: URLSearchParams): number
{
	const v = p.get("pageSize");

	if (v === null) { return kPageSizeDefault; }

	const n = Number.parseInt(v, 10);

	return kPageSizeOptions.includes(n) ? n : kPageSizeDefault;
}

function dateInputToStartMs(v: string): number | null
{
	if (v === "") { return null; }

	const ms = Date.parse(`${v}T00:00:00`);

	return Number.isFinite(ms) ? ms : null;
}

function dateInputToEndMs(v: string): number | null
{
	if (v === "") { return null; }

	const ms = Date.parse(`${v}T23:59:59.999`);

	return Number.isFinite(ms) ? ms : null;
}

function formatDateTime(ms: number): string
{
	const d = new Date(ms);
	const pad = (n: number) => String(n).padStart(2, "0");

	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
